<script setup>
import { defineProps, ref, computed, watch, nextTick } from 'vue';
import { Modal, Popover, AppButton, RadioButton } from '@n11/so-ui-components';
import { OFFER_STATUS, DEAL_TYPES } from '@/utils/enum/offerStatus';
import CurrencyInput from '@/components/DataTable/CurrencyInput';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import {
  sendProductDealEvent,
  sendProductDealViewPromotionEvent,
  sendProductDealCancelCloseClickEvent,
  normalizeGa4Price,
} from '@/utils/ga4/helper';
import { formatNumber } from '@/utils/formatNumber';
import { GA4_EVENT } from '@/utils/ga4/event';
import {
  modifyDeal,
  applyDeal,
  rejectOffer,
} from '../services/productDealService';

const SPECIAL_COMMISSION_HOME_SOURCE = 'specialCommissionDashboard';

const acceptOfferModalRef = ref(null);
const revokeModalRef = ref(null);
const acceptNewOfferModalRef = ref(null);
const umbrellaCampaignModalRef = ref(null);
const staleTemplateDealModalRef = ref(null);
const popoverRef = ref(null);
const newSalesPrice = ref(null);
const errorText = ref();
const isLoading = ref(false);
const store = useStore();
const router = useRouter();
const selectedOffer = ref('');
const currencyInput = ref(null);
const umbrellaCampaignInfo = ref({ title: '', message: '' });
const isRevokeConfirmed = ref(false);
const isNewDealEdit = ref(false);
const isNewOfferConfirmed = ref(false);
const isFromSpecialCommissionDashboard = ref(false);
const isAcceptOfferConfirmed = ref(false);
const isAcceptOfferDismissedByAction = ref(false);
const sentViewPromotionForPendingDealId = ref(null);
const staleTemplateDealInfo = ref({
  message: '',
});
const shouldRefreshDealTableAfterStaleModalClose = ref(false);
const isViewPromotionFromScratch = ref(false);

const props = defineProps({
  // Same type with OfferStatus. Because it is determined according to offers.
  dealStatus: {
    type: String,
    required: false,
    default: OFFER_STATUS.WAITING_FOR_ACCEPTANCE,
  },
  dealType: {
    type: String,
    required: false,
    default: DEAL_TYPES.DEAL,
  },
  startDate: {
    type: Date,
    required: false,
    default: null,
  },
  finishDate: {
    type: Date,
    required: false,
    default: null,
  },
  offerAcceptedDate: {
    type: Date,
    required: false,
    default: null,
  },
  productName: {
    type: String,
    required: false,
    default: '',
  },
  stockCount: {
    type: Number,
    required: false,
    default: 0,
  },
  commission: {
    type: Number,
    required: false,
    default: 0,
  },
  price: {
    type: Number,
    required: false,
    default: 0,
  },
  dealId: {
    type: Number,
    required: false,
    default: 0,
  },
  groupId: {
    type: Number,
    required: false,
    default: 0,
  },
  categoryId: {
    type: Number,
    required: false,
    default: 0,
  },
  productId: {
    type: Number,
    required: false,
    default: 0,
  },
  commissionRates: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  dealStatuses: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  hasVariants: {
    type: Boolean,
    required: false,
    default: false,
  },
  index: {
    type: Number,
    required: false,
    default: 0,
  },
});

const offerTitles = {
  advantageousOffer: 'Avantajlı Teklif',
  superOffer: 'Süper Teklif',
  shockingOffer: 'Kaçmaz Teklif',
};
const productDealErrorCodes = {
  STALE_TEMPLATE_DEAL: 'PRODUCT_DEAL.staleTemplateDeal',
  UMBRELLA_CAMPAIGN_ACTIVE: 'PRODUCT_DEAL.umbrellaCampaignActive',
  ACTIVE_OFFER_EXISTS: 'PRODUCT_DEAL.activeOfferExists',
  DEAL_SPECIAL_COMMISSION_ERROR: 'PRODUCT_DEAL.dealSpecialCommissionError',
};
const defaultStaleTemplateDealMessage =
  'İşlem yapmak istediğiniz teklif artık geçerli değil';

const DEAL_LABELS = {
  current: 'Mevcut Fiyat & Komisyon',
  new: 'Yeni Fiyat & Komisyon',
};

const OFFER_KEYS = ['advantageousOffer', 'superOffer', 'shockingOffer'];

const sellerInfo = computed(() => ({
  sellerId: String(store.state.auth.user?.sellerId ?? ''),
  sellerName: store.state.auth.user?.username ?? '',
}));

const dynamicPosition = computed(() => {
  if (props.index > 5) {
    return 'top-center';
  }

  return 'bottom-center';
});

const selectOffer = (offer) => {
  const rates = props.commissionRates[offer];
  if (!rates) return;

  selectedOffer.value = offer;

  newSalesPrice.value =
    offer === 'shockingOffer' ? rates.upperPrice : rates.lowerPrice;
  currencyInput.value?.setValue(newSalesPrice.value);

  sendProductDealEvent({
    event: GA4_EVENT.SELECT_PROMOTION,
    page_type: 'Ürün Komisyon Teklifleri',
    deal_type: getOfferTitle(offer),
    deal_info: `${getOfferTitle(offer)} | ${rates.upperPrice}-${
      rates.lowerPrice
    } | %${rates.commission}`,
    deal_start_date: getDealStartDate(),
    deal_end_date: formatDealDate(props.finishDate),
    item_name: props.productName ?? '',
    item_stock: String(props.stockCount ?? ''),
    item_id: String(props.productId ?? ''),
    item_category: String(props.categoryId ?? ''),
    deal_id: String(props.dealId ?? ''),
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });
};

const resetOfferPopup = () => {
  if (getbuttonText() === 'Yeni Teklif Seç') {
    isNewDealEdit.value = true;
    sendProductDealEvent({
      event: GA4_EVENT.CHOOSE_NEW_DEAL_CLICK,
      ...buildDealCancelAndOfferPayload(),
    });
  } else {
    isNewDealEdit.value = false;
    sendProductDealEvent({
      event: GA4_EVENT.CHOOSE_DEAL_CLICK,
      ...buildDealCancelAndOfferPayload(),
    });
  }

  newSalesPrice.value = null;
  selectedOffer.value = '';
  errorText.value = '';
};

const currencyInputOptions = {
  locale: 'tr-TR',
  currency: 'TRY',
  precision: 2,
  currencyDisplay: 'symbol',
  hideCurrencySymbolOnFocus: false,
  hideGroupingSeparatorOnFocus: false,
  hideNegligibleDecimalDigitsOnFocus: false,
  autoDecimalDigits: false,
  useGrouping: true,
  accountingSign: false,
  valueRange: { min: 0 },
};

const getCurrentSalesPrice = () =>
  currencyInput.value?.numberValue ?? newSalesPrice.value ?? 0;

const getOfferTitle = (key) => offerTitles[key];

const formatPlannedDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const datePart = d.toLocaleString('tr-TR', { day: 'numeric', month: 'long' });
  const timePart = d.toLocaleString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${datePart}, ${timePart}`;
};

const formatDealDate = (date) => {
  if (!date) return '';

  const d = new Date(date);
  const datePart = d.toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return `${datePart}, ${timePart}`;
};

const getDealStartDate = (formatter = formatDealDate) => {
  if (props.offerAcceptedDate) {
    return formatter(props.offerAcceptedDate);
  }
  return formatter(props.startDate);
};

const getDefaultPriceForOffer = (offerKey) => {
  const rates = props.commissionRates?.[offerKey];
  if (!rates) return null;

  return offerKey === 'shockingOffer' ? rates.upperPrice : rates.lowerPrice;
};

const getChooseDealType = () => {
  const pending = store.state.productDeal.pendingDealOfferAction;
  const offerKey = selectedOffer.value || pending?.offerKey;
  if (!offerKey) return '';

  const defaultPrice = getDefaultPriceForOffer(offerKey);
  const appliedPrice = newSalesPrice.value ?? pending?.newSalesPrice;
  if (defaultPrice == null || appliedPrice == null) return '';

  return Math.abs(Number(appliedPrice) - Number(defaultPrice)) < 0.01
    ? 'Selected'
    : 'Custom';
};

const resolveOfferKeyByPrice = (price) => {
  if (price == null) return null;

  for (const offer of OFFER_KEYS) {
    const rates = props.commissionRates?.[offer];
    if (!rates) continue;

    const { upperPrice, lowerPrice } = rates;
    if (upperPrice >= price && price >= lowerPrice) {
      return offer;
    }
  }

  return null;
};

const toGa4Price = (price) => normalizeGa4Price(price);

const buildDealCancelAndOfferPayload = () => {
  const activeOfferKey = OFFER_KEYS.find(
    (key) =>
      props.dealStatuses?.[key] &&
      props.dealStatuses[key] !== OFFER_STATUS.WAITING_FOR_ACCEPTANCE &&
      props.dealStatuses[key] !== OFFER_STATUS.REFUSED,
  );

  return {
    page_type: 'Ürün Komisyon Teklifleri',
    current_deal_label: DEAL_LABELS.current,
    current_deal_name: activeOfferKey ? getOfferTitle(activeOfferKey) : '',
    current_price: toGa4Price(props.price),
    current_commission: String(props.commission ?? ''),
    deal_start_date: getDealStartDate(),
    deal_end_date: formatDealDate(props.finishDate),
    item_name: props.productName ?? '',
    item_stock: String(props.stockCount ?? ''),
    item_id: String(props.productId ?? ''),
    item_category: String(props.categoryId ?? ''),
    deal_id: String(props.dealId ?? ''),
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  };
};

const buildViewPromotionPayload = (
  isFromScratch,
  eventName,
  acceptOfferDate,
) => {
  const currentOfferKey = resolveOfferKeyByPrice(props.price);
  const payload = {
    page_type: 'Ürün Komisyon Teklifleri',
    event: eventName ?? '',
    current_deal_label: DEAL_LABELS.current,
    current_deal_name: isFromScratch
      ? ''
      : (getOfferTitle(currentOfferKey) ?? ''),
    current_price: toGa4Price(props.price),
    current_commission: String(props.commission ?? ''),
    new_deal_label: DEAL_LABELS.new,
    new_deal_name: getOfferTitle(selectedOffer.value) ?? '',
    new_price: toGa4Price(newSalesPrice.value),
    new_commission: String(newCommissionRate()),
    choose_deal_type: getChooseDealType(),
    deal_start_date: acceptOfferDate
      ? formatDealDate(acceptOfferDate)
      : getDealStartDate(),
    deal_end_date: formatDealDate(props.finishDate),
    item_name: props.productName ?? '',
    item_stock: String(props.stockCount ?? ''),
    item_id: String(props.productId ?? ''),
    item_category: String(props.categoryId ?? ''),
    deal_id: String(props.dealId ?? ''),
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  };

  if (eventName === GA4_EVENT.DEAL_SUCCESS) {
    payload.button_name = 'Onayla';
  }

  return payload;
};

const formatPrice = (price) => {
  return price.toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
  });
};

const getbuttonText = () => {
  if (
    (props.dealStatus === OFFER_STATUS.WAITING_FOR_ACCEPTANCE ||
      props.dealStatus === OFFER_STATUS.REFUSED) &&
    props.dealType !== DEAL_TYPES.NEW_DEAL
  ) {
    return 'Teklif Seç';
  }

  return 'Yeni Teklif Seç';
};

const addNewOffer = () => {
  if (popoverRef.value) {
    popoverRef.value.popoverToogle();
  }

  const isAllWaitingForAcceptance = Object.values(props.dealStatuses).every(
    (value) => value === undefined || value === 'WAITING_FOR_ACCEPTANCE',
  );

  isViewPromotionFromScratch.value = isAllWaitingForAcceptance;

  if (isAllWaitingForAcceptance) {
    acceptOfferModalRef.value.openModal();
  } else {
    acceptNewOfferModalRef.value.openModal();
  }

  sendProductDealEvent(
    buildViewPromotionPayload(
      isAllWaitingForAcceptance,
      GA4_EVENT.VIEW_PROMOTION,
    ),
  );
};

const handleContinue = () => {
  const offer = selectedOffer.value;
  if (!offer) {
    errorText.value = `Bir teklif seçmelisiniz.`;
    return;
  }

  if (!isNewDealEdit.value) {
    sendProductDealEvent({
      event: GA4_EVENT.DEAL_APPLY,
      page_type: 'Ürün Komisyon Teklifleri',
      button_name: 'Devam Et',
      deal_price: formatNumber(getCurrentSalesPrice()),
      choose_deal_type: getChooseDealType(),
      deal_type: getOfferTitle(selectedOffer.value) ?? '',
      deal_start_date: getDealStartDate(),
      deal_end_date: formatDealDate(props.finishDate),
      item_name: props.productName ?? '',
      item_stock: String(props.stockCount ?? ''),
      item_id: String(props.productId ?? ''),
      item_category: String(props.categoryId ?? ''),
      deal_id: String(props.dealId ?? ''),
      seller_name: sellerInfo.value.sellerName,
      seller_id: sellerInfo.value.sellerId,
    });
  } else {
    sendProductDealEvent({
      event: GA4_EVENT.DEAL_START_EDIT,
      page_type: 'Ürün Komisyon Teklifleri',
      button_name: 'Devam Et',
      deal_price: formatNumber(getCurrentSalesPrice()),
      choose_deal_type: getChooseDealType(),
      deal_type: getOfferTitle(selectedOffer.value) ?? '',
      deal_start_date: getDealStartDate(formatPlannedDate),
      deal_end_date: formatPlannedDate(props.finishDate),
      item_name: props.productName ?? '',
      item_stock: String(props.stockCount ?? ''),
      item_id: String(props.productId ?? ''),
      item_category: String(props.categoryId ?? ''),
      deal_id: String(props.dealId ?? ''),
      seller_name: sellerInfo.value.sellerName,
      seller_id: sellerInfo.value.sellerId,
    });
  }

  const { upperPrice, lowerPrice } = props.commissionRates[offer];
  if (newSalesPrice.value > upperPrice) {
    errorText.value = `Bu teklif için fiyat en fazla ₺${upperPrice} olmalıdır.`;
  } else if (newSalesPrice.value < lowerPrice) {
    errorText.value = `Bu teklif için fiyat en az ₺${lowerPrice} olmalıdır.`;
  } else {
    errorText.value = '';
    addNewOffer();
  }
};

const getFirstError = (payload) =>
  payload?.data?.errors?.[0] ||
  payload?.errors?.[0] ||
  payload?.response?.data?.errors?.[0];
const refreshDealTable = () =>
  store.commit('common/TRIGGER_DEAL_TABLE_REFRESH', Date.now());
const showToast = ({ type = 'info', title = '', description }) => {
  store.commit('common/SET_TOASTER_DATA', {
    type,
    title,
    description,
  });
};

const handleStaleTemplateDealError = (payload) => {
  const firstError = getFirstError(payload);
  if (firstError?.code !== productDealErrorCodes.STALE_TEMPLATE_DEAL) {
    return false;
  }

  staleTemplateDealInfo.value = {
    message: firstError?.message || defaultStaleTemplateDealMessage,
  };
  shouldRefreshDealTableAfterStaleModalClose.value = true;
  staleTemplateDealModalRef.value.openModal();
  return true;
};

const closeStaleTemplateDealModal = () => {
  staleTemplateDealModalRef.value.hiddenModal();
};

const refreshDealTableAfterStaleModalClose = () => {
  if (shouldRefreshDealTableAfterStaleModalClose.value) {
    shouldRefreshDealTableAfterStaleModalClose.value = false;
    refreshDealTable();
  }
};

const handleAcceptOfferNegativeClick = () => {
  const cameFromSpecialCommissionDashboard =
    isFromSpecialCommissionDashboard.value;
  isAcceptOfferDismissedByAction.value = true;
  acceptOfferModalRef.value.hiddenModal();
  if (cameFromSpecialCommissionDashboard) {
    router.push({ name: 'Home' });
  }
};

const sendViewPromotionCloseCancelEvent = (buttonName = 'Geri Dön') => {
  if (isFromSpecialCommissionDashboard.value) {
    const payload = buildViewPromotionPayload(
      false,
      GA4_EVENT.HP_DEAL_CANCEL_CLOSE_CLICK,
    );
    sendProductDealCancelCloseClickEvent({
      ...payload,
      button_name: buttonName,
      item_category_id: String(props.categoryId ?? ''),
    });
    return;
  }

  sendProductDealEvent(
    buildViewPromotionPayload(
      isViewPromotionFromScratch.value,
      GA4_EVENT.DEAL_CLOSE_CANCEL_CLICK,
    ),
  );

  if (isNewDealEdit.value) {
    sendProductDealEvent({
      event: GA4_EVENT.DEAL_EDIT_CLOSE_CANCEL_CLICK,
      ...buildViewPromotionPayload(
        false,
        GA4_EVENT.DEAL_EDIT_CLOSE_CANCEL_CLICK,
      ),
    });
  }
};

const handleAcceptOfferModalClosed = () => {
  if (!isAcceptOfferConfirmed.value) {
    const buttonName = isAcceptOfferDismissedByAction.value ? 'Geri Dön' : 'X';
    sendViewPromotionCloseCancelEvent(buttonName);
  }
  isAcceptOfferConfirmed.value = false;
  isAcceptOfferDismissedByAction.value = false;
  clearAcceptOfferModalState();
};

const clearAcceptOfferModalState = () => {
  store.commit('productDeal/CLEAR_PENDING_DEAL_OFFER_ACTION');
  selectedOffer.value = '';
  newSalesPrice.value = null;
  errorText.value = '';
  isFromSpecialCommissionDashboard.value = false;
  sentViewPromotionForPendingDealId.value = null;
};

const prependAppliedDealToTable = () => {
  const pending = store.state.productDeal.pendingDealOfferAction;
  const offerKey = selectedOffer.value || pending?.offerKey;
  const acceptedPrice = newSalesPrice.value ?? pending?.newSalesPrice;

  store.commit('productDeal/SET_APPLIED_DEAL_ROW_UPDATE', {
    dealDetails: pending?.dealDetails,
    offerKey,
    price: acceptedPrice,
    newSalesPrice: acceptedPrice,
    commission:
      newCommissionRate() ??
      (offerKey && pending?.dealDetails?.[offerKey]?.commission),
  });
};

const handleModifyDeal = async () => {
  isLoading.value = true;
  isNewOfferConfirmed.value = true;

  try {
    const response = await modifyDeal({
      dealId: props.dealId,
      groupId: props.groupId,
      price: newSalesPrice.value,
      categoryId: props.categoryId,
      productId: props.productId,
    });

    if (handleStaleTemplateDealError(response)) {
      acceptNewOfferModalRef.value.hiddenModal();
      isLoading.value = false;
      return;
    }

    if (isNewDealEdit.value) {
      sendProductDealEvent(
        buildViewPromotionPayload(
          false,
          GA4_EVENT.DEAL_START_EDIT_SUCCESS,
          response?.data?.acceptOfferDate,
        ),
      );
    }

    sendProductDealEvent(
      buildViewPromotionPayload(
        false,
        GA4_EVENT.DEAL_SUCCESS,
        response?.data?.acceptOfferDate,
      ),
    );

    showToast({
      type: 'success',
      title: '',
      description: 'Seçtiğiniz teklif başarıyla uygulandı.',
    });

    refreshDealTable();
    acceptNewOfferModalRef.value.hiddenModal();
  } catch (error) {
    if (handleStaleTemplateDealError(error)) {
      acceptNewOfferModalRef.value.hiddenModal();
      isLoading.value = false;
      return;
    }

    if (error?.data?.errors?.[0]?.message) {
      showToast({
        type: 'info',
        description: error.data.errors[0].message,
      });
    } else {
      showToast({
        type: 'info',
        description: 'Teknik bir hata oluştu.',
      });
    }
    acceptNewOfferModalRef.value.hiddenModal();
  }
  isLoading.value = false;
};

const tryOpenPendingSpecialCommissionOffer = () => {
  const pending = store.state.productDeal.pendingDealOfferAction;
  if (
    !pending ||
    pending.dealId !== props.dealId ||
    pending.source !== SPECIAL_COMMISSION_HOME_SOURCE
  ) {
    return;
  }

  selectedOffer.value = pending.offerKey;
  newSalesPrice.value = pending.newSalesPrice;
  isFromSpecialCommissionDashboard.value = true;
  isViewPromotionFromScratch.value = false;

  nextTick(() => {
    acceptOfferModalRef.value?.openModal();

    if (
      pending.viewPromotionParams &&
      sentViewPromotionForPendingDealId.value !== pending.dealId
    ) {
      sendProductDealViewPromotionEvent(pending.viewPromotionParams);
      sentViewPromotionForPendingDealId.value = pending.dealId;
    }
  });
};

watch(
  [() => props.dealId, () => store.state.productDeal.pendingDealOfferAction],
  () => {
    tryOpenPendingSpecialCommissionOffer();
  },
  { immediate: true },
);

const handleRejectOffer = async () => {
  isLoading.value = true;
  isRevokeConfirmed.value = true;

  try {
    const response = await rejectOffer({
      dealId: props.dealId,
      groupId: props.groupId,
    });

    sendProductDealEvent({
      event: GA4_EVENT.DEAL_CANCEL_SUCCESS,
      ...buildDealCancelAndOfferPayload(),
      deal_start_date: response?.data?.acceptOfferDate
        ? formatDealDate(response.data.acceptOfferDate)
        : getDealStartDate(),
      deal_end_date: response?.data?.revokeOfferDate
        ? formatDealDate(response.data.revokeOfferDate)
        : formatDealDate(props.finishDate),
    });

    showToast({
      type: 'success',
      title: '',
      description: 'Tekliften başarıyla vazgeçtiniz.',
    });

    store.commit('common/TRIGGER_DEAL_TABLE_REFRESH', Date.now());
    revokeModalRef.value.hiddenModal();
  } catch (error) {
    if (error?.data?.errors?.[0]?.message) {
      showToast({
        type: 'info',
        description: error.data.errors[0].message,
      });
    } else {
      showToast({
        type: 'info',
        description: 'Teknik bir hata oluştu.',
      });
    }
    revokeModalRef.value.hiddenModal();
  }
  isLoading.value = false;
};

const handleApplyDeal = async () => {
  isLoading.value = true;
  isAcceptOfferConfirmed.value = true;
  try {
    const response = await applyDeal({
      dealId: props.dealId,
      groupId: props.groupId,
      price: newSalesPrice.value,
      productId: props.productId,
    });

    if (handleStaleTemplateDealError(response)) {
      acceptOfferModalRef.value.hiddenModal();
      isLoading.value = false;
      return;
    }

    sendProductDealEvent(
      buildViewPromotionPayload(
        false,
        GA4_EVENT.DEAL_SUCCESS,
        response?.data?.acceptOfferDate,
      ),
    );

    showToast({
      type: 'success',
      title: '',
      description: 'Seçtiğiniz teklif başarıyla uygulandı.',
    });

    if (isFromSpecialCommissionDashboard.value) {
      prependAppliedDealToTable();
    } else {
      refreshDealTable();
    }
    isFromSpecialCommissionDashboard.value = false;
    acceptOfferModalRef.value.hiddenModal();
  } catch (error) {
    isLoading.value = false;
    acceptOfferModalRef.value.hiddenModal();
    isFromSpecialCommissionDashboard.value = false;

    if (handleStaleTemplateDealError(error)) {
      return;
    }

    if (
      error.data?.errors?.[0]?.code === 'PRODUCT_DEAL.umbrellaCampaignActive'
    ) {
      umbrellaCampaignInfo.value = {
        title: error.data?.errors?.[0]?.title || '',
        message: error.data?.errors?.[0]?.message || '',
      };
      umbrellaCampaignModalRef.value.openModal();
    } else if (
      error.data?.errors?.[0]?.code === 'PRODUCT_DEAL.activeOfferExists'
    ) {
      store.commit('common/SET_TOASTER_DATA', {
        type: 'info',
        description: error.data?.errors?.[0]?.message,
      });
    } else if (
      error.data?.errors?.[0]?.code ===
      'PRODUCT_DEAL.dealSpecialCommissionError'
    ) {
      store.commit('common/SET_TOASTER_DATA', {
        type: 'info',
        description: error.data?.errors?.[0]?.message,
      });
    } else if (error.data?.errors?.[0]?.message) {
      store.commit('common/SET_TOASTER_DATA', {
        type: 'info',
        description: error.data?.errors?.[0]?.message,
      });
    } else {
      store.commit('common/SET_TOASTER_DATA', {
        type: 'info',
        description: 'Teknik bir hata oluştu.',
      });
    }
  }
  isLoading.value = false;
};
const newCommissionRate = () => {
  const offerKey = resolveOfferKeyByPrice(newSalesPrice.value);
  if (offerKey && props.commissionRates[offerKey]) {
    return props.commissionRates[offerKey].commission;
  }
  return props.commission;
};

const handleRevokeNegativeClick = () => {
  revokeModalRef.value.hiddenModal();
};

const handleRevokeModalClosed = () => {
  if (!isRevokeConfirmed.value) {
    sendProductDealEvent({
      event: GA4_EVENT.DEAL_CANCEL_RETURN,
      ...buildDealCancelAndOfferPayload(),
    });
  }
  isRevokeConfirmed.value = false;
};

const handleRevokeClick = () => {
  if (popoverRef.value?.showPopover) {
    popoverRef.value.popoverToogle();
  }

  sendProductDealEvent({
    event: GA4_EVENT.DEAL_CANCEL,
    ...buildDealCancelAndOfferPayload(),
  });

  revokeModalRef.value.openModal();
};

const handleNewOfferModalNegativeClick = () => {
  acceptNewOfferModalRef.value.hiddenModal();
};

const handleNewOfferModalClosed = () => {
  if (!isNewOfferConfirmed.value) {
    sendViewPromotionCloseCancelEvent();
  }
  isNewOfferConfirmed.value = false;
};
</script>

<template>
  <div v-if="!props.commission" class="seller-price-offer">
    <span class="not-exists">-</span>
    <p class="not-exists">Teklif bulunmuyor.</p>
  </div>
  <div v-else class="seller-price-offer">
    <p
      v-if="props.dealType == DEAL_TYPES.BO_DELETED_DEAL"
      class="text deleted-deal"
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="6" fill="#D13333" />
        <path
          d="M4 6L8 6"
          stroke="white"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </svg>
      n11 Tarafından Sonlandırıldı
    </p>
    <p
      v-else-if="props.dealType === DEAL_TYPES.NEW_DEAL"
      class="text updated-deal"
    >
      <svg width="13" height="14" viewBox="0 0 13 14" fill="none">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M7.15833 0.196826C6.96337 0.068838 6.73467 0 6.49992 0L6.38317 0.00569108C6.15121 0.0283645 5.92994 0.118241 5.74719 0.265508L4.66751 1.13629L3.28416 1.06576L3.16718 1.06561C2.93422 1.07663 2.70893 1.1552 2.51913 1.29293L2.42809 1.36602C2.25386 1.52042 2.1277 1.72285 2.06633 1.94909L1.70418 3.28601L0.543434 4.0442L0.448838 4.11282C0.267128 4.2586 0.131285 4.45439 0.0588149 4.6771L0.0281683 4.78961C-0.021955 5.01666 -0.00509705 5.25432 0.0780352 5.47328L0.571965 6.76886L0.413503 7.18528L0.389554 7.26088C0.305872 7.59028 0.480702 7.93734 0.806163 8.06136C0.832954 8.07156 0.88645 8.10848 0.962508 8.16809L3.91613 4.42202C4.53387 3.63856 5.6908 3.59715 6.36583 4.29477L6.44445 4.38203L6.48621 4.43531L6.57266 4.55721L9.41289 9.02869C9.92372 9.83291 9.63494 10.8881 8.76497 11.3542L8.6578 11.4072L8.52327 11.4566L6.58283 12.0627L6.58879 12.1249C6.59527 12.2383 6.59478 12.3523 6.58719 12.4662L6.5705 12.637L6.54309 12.8071C6.51172 12.9695 6.46611 13.1275 6.40705 13.2797C6.65006 13.4689 6.99977 13.4739 7.25008 13.2724L8.32904 12.4035L9.71412 12.4725L9.83087 12.4727C10.0635 12.4617 10.2891 12.383 10.4791 12.2449L10.5702 12.1717C10.7445 12.0169 10.8706 11.814 10.9316 11.5873L11.2951 10.2504L12.4556 9.49622L12.5505 9.42743C12.7328 9.28132 12.8688 9.08533 12.9413 8.86238L12.9719 8.74975C13.022 8.52246 13.005 8.28457 12.9216 8.06547L12.428 6.77022L12.9215 5.47573L12.9577 5.36468C13.019 5.14003 13.0137 4.90163 12.9412 4.67873L12.8997 4.56964C12.8065 4.35638 12.6528 4.17401 12.4564 4.04587L11.2958 3.28872L10.9336 1.95107L10.8975 1.83993C10.815 1.62212 10.6705 1.43234 10.4807 1.29461L10.383 1.23071C10.1821 1.11294 9.95048 1.05571 9.71623 1.06741L8.33176 1.13561L7.25288 0.265704L7.15833 0.196826Z"
          fill="#6CB9F0"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M0.165846 10.9097L4.78424 5.0522C5.04504 4.72143 5.55461 4.74736 5.78044 5.10289L8.62067 9.57439C8.83616 9.91366 8.67085 10.3649 8.2871 10.4848L5.39652 11.3886C5.61563 11.7565 5.69233 12.1926 5.61089 12.6141C5.52629 13.0519 5.27743 13.4409 4.90667 13.7075C4.72299 13.8348 4.51519 13.9234 4.29604 13.9676C4.07688 14.0118 3.851 14.0108 3.63226 13.9646C3.41351 13.9184 3.20652 13.828 3.02399 13.699C2.87797 13.5957 2.75007 13.4695 2.65002 13.3332L2.58036 13.2293L2.13859 12.5509C2.11268 12.5111 2.09199 12.4695 2.07637 12.4267L1.32976 12.6601C1.06584 12.7426 0.779354 12.6378 0.631159 12.4045L0.129883 11.6152C-0.00917904 11.3962 0.00523892 11.1134 0.165846 10.9097ZM3.27269 12.0526L3.61454 12.578C3.6442 12.6266 3.6835 12.6686 3.73 12.7015C3.77651 12.7344 3.82925 12.7574 3.88498 12.7692C3.94072 12.7809 3.99827 12.7812 4.05411 12.7699C4.10995 12.7587 4.1629 12.7361 4.20113 12.7097C4.31014 12.6313 4.38505 12.5142 4.41051 12.3824C4.43089 12.277 4.41838 12.1685 4.38244 12.0815L4.35124 12.0206L4.26904 11.9016C4.23873 11.8578 4.21495 11.8113 4.19748 11.7635L3.27269 12.0526Z"
          fill="#2B86D1"
        />
      </svg>
      Teklif Yenilendi
    </p>
    <p class="text">Mevcut satış fiyatı ve komisyon:</p>
    <p class="price">{{ formatPrice(props.price) }}</p>
    <p class="commission">%{{ props.commission }} Komisyon</p>
    <div
      v-if="props.dealType != DEAL_TYPES.BO_DELETED_DEAL"
      class="popover-wrapper"
    >
      <Popover
        ref="popoverRef"
        :position="dynamicPosition"
        color="#ffffff"
        :with-overlay="true"
        :z-index="10001"
        to="body"
      >
        <template #content="{ on }">
          <AppButton
            :title="getbuttonText()"
            primary
            sm
            class="popover-wrapper__btn mt-3"
            v-on="on"
            @click="resetOfferPopup"
          />
          <AppButton
            v-if="
              (dealStatus !== OFFER_STATUS.WAITING_FOR_ACCEPTANCE &&
                dealStatus !== OFFER_STATUS.REFUSED) ||
              (dealType === DEAL_TYPES.NEW_DEAL &&
                dealStatus !== OFFER_STATUS.REFUSED)
            "
            :title="'Tekliften Vazgeç'"
            secondary
            sm
            class="popover-wrapper__btn"
            @click="handleRevokeClick"
          />
        </template>

        <template #popoverContent>
          <div class="popover-wrapper__content">
            <div class="popover-wrapper__content__header">
              <p class="popover-wrapper__content__title">Teklif Seçin</p>
              <div v-if="hasVariants">
                <p class="all-variants-badge">Tüm Varyantlarda Geçerli</p>
              </div>
              <div v-else>
                <p class="all-variants-badge">Tek Varyantta Geçerli</p>
              </div>
            </div>
            <div class="popover-wrapper__current">
              <p class="current-title">Mevcut satış fiyatı ve komisyon:</p>
              <div class="current-details">
                <p class="current-details__price">
                  {{ formatPrice(props.price) }}
                </p>
                <p class="current-details__text">
                  %{{ props.commission }} Komisyon
                </p>
              </div>
            </div>
            <div class="popover-wrapper__list">
              <div
                v-for="(offer, key) in props.commissionRates"
                :key="key"
                class="offer-item"
              >
                <RadioButton
                  name="offer"
                  :pre-selected="selectedOffer === key"
                  :is-input-full="true"
                  @select="selectOffer(key)"
                >
                  <div class="offer-item__content">
                    <span class="offer-item__title">{{
                      getOfferTitle(key)
                    }}</span>
                    <div class="current-values">
                      <p class="current-values__price">
                        {{ formatPrice(offer.upperPrice) }}
                        <template v-if="offer.lowerPrice > 0">
                          - {{ formatPrice(offer.lowerPrice) }}</template
                        >
                        <template v-else> ve altı</template>
                      </p>
                      <p class="commission">%{{ offer.commission }} Komisyon</p>
                    </div>
                  </div>
                </RadioButton>
              </div>
            </div>
            <CurrencyInput
              ref="currencyInput"
              :key="`currency-input-${key}`"
              v-model="newSalesPrice"
              class="popover-wrapper__content__input"
              :options="currencyInputOptions"
              :class="[{ error: errorText }]"
            />
            <p v-if="errorText" class="popover-wrapper__content__error-text">
              {{ errorText }}
            </p>
            <AppButton title="Devam Et" md primary @click="handleContinue" />
          </div>
        </template>
      </Popover>
    </div>

    <Modal
      ref="revokeModalRef"
      title="Tekliften vazgeçmek istediğinize emin misiniz?"
      negative-action-text="Geri Dön"
      positive-action-text="Tekliften Vazgeç"
      :is-positive-action-button-disabled="isLoading"
      :is-positive-action-pending="isLoading"
      :modal-style="{ width: '460px' }"
      @on-positive-action-click="handleRejectOffer"
      @on-negative-action-click="handleRevokeNegativeClick"
      @modal-closed="handleRevokeModalClosed"
    >
      <template #body>
        <div class="accept-body">
          <p v-if="hasVariants">
            Komisyon teklifi
            <span class="accept-body__bold">ürünün tüm varyantları</span>
            için iptal edilecek.
          </p>
          <p v-else>
            Komisyon teklifi
            <span class="accept-body__bold">ürünün tek varyantı</span>
            için iptal edilecek.
          </p>
          <div class="description">
            Geçerlilik tarihleri süresince bu teklife tekrar katılabilirsiniz.
          </div>
        </div>
      </template>
    </Modal>

    <Modal
      ref="acceptOfferModalRef"
      type="success"
      title="Seçiminizi onaylıyor musunuz?"
      negative-action-text="Geri Dön"
      positive-action-text="Onayla"
      :is-positive-action-button-disabled="isLoading"
      :is-positive-action-pending="isLoading"
      @on-positive-action-click="handleApplyDeal"
      @on-negative-action-click="handleAcceptOfferNegativeClick"
      @modal-closed="handleAcceptOfferModalClosed"
    >
      <template #body>
        <div class="accept-body">
          <p v-if="hasVariants">
            Onaylarsanız seçtiğiniz tarih aralığında aşağıdaki yeni fiyat ve
            komisyon oranı, ürünün
            <span class="accept-body__bold">tüm varyantlarında</span>
            geçerli olacaktır.
          </p>
          <p v-else>
            Onaylarsanız seçtiğiniz tarih aralığında aşağıdaki yeni fiyat ve
            komisyon oranı geçerli olacaktır.
          </p>
          <div class="change-offer">
            <div class="change-offer__old">
              <span class="title">{{ DEAL_LABELS.current }}</span>
              <span class="price">{{ formatPrice(price) }}</span>
              <span class="commission">%{{ commission }} Komisyon</span>
            </div>
            <div class="change-offer-new">
              <span class="title">{{ DEAL_LABELS.new }}</span>
              <span class="price">{{ formatPrice(newSalesPrice) }}</span>
              <span class="commission"
                >%{{ newCommissionRate() }} Komisyon</span
              >
            </div>
          </div>
          <div class="description">
            Daha sonra istediğiniz zaman değişiklik yapabilir veya tekliften
            vazgeçebilirsiniz.
          </div>
        </div>
      </template>
    </Modal>

    <Modal
      ref="acceptNewOfferModalRef"
      type="info"
      title="Bu teklife geçiş yapmayı onaylıyor musunuz?"
      negative-action-text="Geri Dön"
      positive-action-text="Onayla"
      :is-positive-action-button-disabled="isLoading"
      :is-positive-action-pending="isLoading"
      @on-positive-action-click="handleModifyDeal"
      @on-negative-action-click="handleNewOfferModalNegativeClick"
      @modal-closed="handleNewOfferModalClosed"
    >
      <template #body>
        <div class="accept-body">
          <p v-if="hasVariants">
            Onaylarsanız seçtiğiniz tarih aralığında aşağıdaki yeni fiyat ve
            komisyon oranı, ürünün
            <span class="accept-body__bold">tüm varyantlarında</span> geçerli
            olacaktır.
          </p>
          <p v-else>
            Onaylarsanız belirtilen tarihlerde geçiş yapacağınız teklif geçerli
            olacak.
          </p>
          <div class="change-offer">
            <div class="change-offer__old">
              <span class="title">{{ DEAL_LABELS.current }}</span>
              <span class="price">{{ formatPrice(price) }}</span>
              <span class="commission">%{{ commission }} Komisyon</span>
            </div>
            <div class="change-offer-new">
              <span class="title">{{ DEAL_LABELS.new }}</span>
              <span class="price">{{ formatPrice(newSalesPrice) }}</span>
              <span class="commission"
                >%{{ newCommissionRate() }} Komisyon</span
              >
            </div>
          </div>
          <div class="description">
            Daha sonra istediğiniz zaman değişiklik yapabilir veya tekliften
            vazgeçebilirsiniz.
          </div>
        </div>
      </template>
    </Modal>

    <Modal
      ref="umbrellaCampaignModalRef"
      type="info"
      :title="umbrellaCampaignInfo.title"
      positive-action-text="Anladım"
      @on-positive-action-click="umbrellaCampaignModalRef.hiddenModal()"
    >
      <template #body>
        <p class="umbrella-campaign-message">
          {{ umbrellaCampaignInfo.message }}
        </p>
      </template>
    </Modal>

    <Modal
      ref="staleTemplateDealModalRef"
      type="info"
      positive-action-text="Tamam"
      :modal-style="{ width: '445px' }"
      @on-positive-action-click="closeStaleTemplateDealModal"
      @modal-closed="refreshDealTableAfterStaleModalClose"
    >
      <template #body>
        <p class="stale-template-deal-message">
          {{ staleTemplateDealInfo.message }}
        </p>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.seller-price-offer {
  height: 100%;
  padding: 16px 12px;

  span {
    &.not-exists {
      color: $colorN60;
      font-size: 13px;
      font-weight: 600;
      line-height: 18px;
    }
  }

  p {
    &.not-exists {
      color: $colorN60;
      font-size: 10px;
      font-weight: 600;
      line-height: 12px;
    }

    &.text {
      margin-bottom: 4px;
      color: $colorD50;
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
    }

    &.price {
      margin-bottom: 4px;
      color: $colorN80;
      font-size: 13px;
      font-weight: 700;
      line-height: 18px;
    }

    &.commission {
      display: inline-flex;
      padding: 2px 6px;
      border: 1px solid $colorN30;
      border-radius: 4px;
      background-color: $colorN00;
      color: $colorN60;
      font-size: 11px;
      font-weight: 600;
      line-height: 14px;
    }

    &.deleted-deal {
      display: flex;
      width: fit-content;
      align-items: center;
      padding: 3px;
      border: 1px solid $colorE20;
      border-radius: 4px;
      background: $colorN00;
      color: $colorE60;
      font-size: 11px;
      font-weight: 600;
      gap: 4px;
      line-height: 14px;
    }

    &.updated-deal {
      display: flex;
      width: 118px;
      align-items: center;
      padding: 3px 8px;
      border: 1px solid $colorB20;
      border-radius: 4px;
      color: $colorB60;
      font-size: 11px;
      font-weight: 600;
      gap: 4px;
      line-height: 14px;
    }
  }

  .umbrella-campaign-message {
    color: $colorN80;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: -0.05px;
    line-height: 24px;
    text-align: center;
  }

  .stale-template-deal-message {
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.2px;
    line-height: 26px;
    text-align: center;
  }

  .accept-body {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &__bold {
      font-weight: 700 !important;
    }

    p {
      color: $colorN80;
      font-size: 16px;
      font-weight: 400;
      letter-spacing: -0.05px;
      line-height: 24px;
    }

    .change-offer {
      display: flex;
      align-items: center;
      align-self: stretch;
      padding-top: 10px;
      gap: 20px;

      &__old {
        background-color: $colorD05;

        &::after {
          position: absolute;
          z-index: 9;
          right: -22px;
          width: 24px;
          height: 24px;
          background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgaWQ9ImFycm93X3dhcm1fdXBfMjRkcF81RjYzNjhfRklMTDBfd2dodDQwMF9HUkFEMF9vcHN6MjQgNiI+CjxnIGlkPSJWZWN0b3IiPgo8cGF0aCBkPSJNNS42IDhIOS4yVjE2SDUuNlY4Wk0yIDhINC40VjE2SDJWOFoiIGZpbGw9IiNEM0Q2REYiLz4KPHBhdGggZD0iTTEwLjQgOEgxN1YxNkgxMC40VjhaIiBmaWxsPSIjRDNENkRGIi8+CjwvZz4KPHBhdGggaWQ9IlBvbHlnb24gMSIgZD0iTTIyIDEyTDEzLjc1IDE5Ljc5NDJMMTMuNzUgNC4yMDU3N0wyMiAxMloiIGZpbGw9IiNEM0Q2REYiLz4KPC9nPgo8L3N2Zz4K');
          content: '';
        }
      }

      > div {
        position: relative;
        display: flex;
        flex: 1 0 0;
        flex-direction: column;
        align-items: center;
        align-self: stretch;
        padding: 24px 24px 16px;
        border: 1px solid $colorD10;
        border-radius: 6px;

        .title {
          position: absolute;
          top: -9px;
          left: 50%;
          display: flex;
          width: max-content;
          align-items: center;
          justify-content: center;
          padding: 4px 8px;
          border: 0.5px solid $colorD20;
          border-radius: 6px;
          background: $colorN00;
          color: $colorD50;
          font-size: 10px;
          font-weight: 600;
          gap: 2px;
          line-height: 12px;
          transform: translateX(-50%);
        }

        .price {
          color: $colorN80;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: -0.05px;
          line-height: 20px;
        }

        .commission {
          display: flex;
          height: 18px;
          flex-shrink: 0;
          align-items: center;
          padding: 2px 4px;
          border: 1px solid $colorD20;
          border-radius: 4px;
          margin-top: 4px;
          background: $colorN00;
          color: $colorD50;
          font-size: 10px;
          font-weight: 600;
          gap: 4px;
          line-height: 12px;
          text-align: center;
        }

        &.change-offer-new {
          border: 1px solid $colorP20;
          background: $colorM05;

          .title {
            background: $colorM50;
            color: white;
            line-height: 10px;
          }

          .price {
            color: $colorM50;
          }

          .commission {
            border: 1px solid $colorP20;
            color: $colorM50;
          }
        }
      }
    }

    .description {
      display: flex;
      align-items: flex-start;
      padding: 16px;
      border-radius: 6px;
      background: $colorB05;
      color: $colorN80;
      font-size: 13px;
      gap: 8px;
      letter-spacing: -0.05px;
      line-height: 18px;
      text-align: start;

      &::before {
        display: block;
        width: 24px;
        min-width: 24px;
        height: 24px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:v='https://vecta.io/nano'%3E%3Ccircle cx='12' cy='12' r='10' fill='%234fa4e5'/%3E%3Cpath d='M12 11a1 1 0 0 1 .993.884L13 12v4a1 1 0 0 1-1.993.116L11 16v-4a1 1 0 0 1 1-1zm0-4a1 1 0 1 1 0 2 1 1 0 1 1 0-2z' fill='%23fff'/%3E%3C/svg%3E");
        content: '';
      }
    }
  }

  .seller-price-offer
    :deep(.modal-wrapper .modal-container .modal-body .content) {
    gap: 16px;
  }

  .offerInformation {
    flex-direction: column;
    gap: 16px !important;

    &__title {
      margin-bottom: 16px;
      color: $colorN80;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: -0.2px;
      line-height: 26px;
      text-align: center;
    }

    &__description {
      color: $colorN80;
      font-size: 16px;
      letter-spacing: -0.05px;
      line-height: 24px;
      text-align: center;
    }
  }

  &__current {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 8px;
    gap: 4px;

    .current-details {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
}

.popover-text.is-teleported {
  padding: 0 !important;

  &.bottom-center {
    top: calc(var(--anchor-top) + var(--anchor-height) + 40px) !important;
    bottom: unset !important;
    left: calc(
      var(--anchor-left) + (var(--anchor-width) / 2) + 20px
    ) !important;
    transform: translate(-50%, 0) !important;
  }

  &.top-center {
    top: calc(var(--anchor-top) - 8px) !important;
    bottom: unset !important;
    left: calc(var(--anchor-left) + (var(--anchor-width) / 2)) !important;
    transform: translate(-50%, -100%) !important;
  }

  .current {
    &-values {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-top: 1px;
      gap: 4px;

      .commission {
        flex-shrink: 0;
      }

      &__price {
        display: flex;
        flex-shrink: 0;
        color: $colorN80;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
      }
    }

    &-details {
      display: flex;
      align-items: center;
      gap: 4px;

      &__price {
        color: $colorN80;
        font-size: 13px;
        font-weight: 700;
        line-height: 18px;
      }

      &__text {
        padding: 2px 6px;
        border: 1px solid $colorN30;
        border-radius: 4px;
        background: $colorN00;
        color: $colorN60;
        font-size: 11px;
        font-weight: 600;
        line-height: 14px;
      }
    }

    &-title {
      margin-bottom: 4px;
      color: $colorN60;
      font-size: 11px;
      line-height: 14px;
      text-align: left;
    }
  }

  .commission-badge,
  .commission {
    padding: 2px 6px;
    border: 1px solid $colorD20;
    border-radius: 4px;
    background-color: $colorN00;
    color: $colorD50 !important;
    font-size: 11px;
    font-weight: 600;
    line-height: 14px;
  }

  .all-variants {
    &-badge {
      width: fit-content;
      padding: 3px 6px;
      border: 1px solid $colorB30;
      border-radius: 4px;
      background-color: $colorN00;
      color: $colorB50 !important;
      font-size: 10px;
      font-weight: 600;
      line-height: 12px;
      white-space: nowrap;
    }

    &-description {
      margin-top: 4px;
      color: $colorD50;
      font-size: 16px;
      font-weight: 400;
      letter-spacing: -0.05px;
      line-height: 30px;
      text-align: left;
    }

    &__bold {
      font-weight: 700 !important;
    }
  }

  .popover-wrapper__list {
    display: flex;
    flex-direction: column;
    gap: 8px;

    :deep(.radioButton-container) {
      width: 100%;

      .radioButton {
        flex-shrink: 0;
      }
    }

    .offer-item {
      &__content {
        display: flex;
        width: 100%;
        flex-direction: column;
        text-align: left;
      }

      &__title {
        color: $colorN60;
        font-size: 11px;
        line-height: 14px;
      }

      &__details {
        display: flex;
        align-items: center;
        gap: 8px;

        .price {
          color: $colorN80;
          font-size: 14px;
          font-weight: 700;
        }
      }
    }
  }
}

.popover-wrapper {
  .popover {
    width: 100%;
  }

  .mt-3 {
    margin-top: 12px !important;
  }

  &__btn {
    height: 14px !important;
    margin-top: 8px;
    font-size: 11px !important;
  }

  &__content {
    display: flex;
    width: 100%;
    max-width: 284px;
    flex-direction: column;
    padding: 12px;
    border: 1px solid var(--Dusk---Decorative-D40, $colorD40);
    border-radius: 8px;
    background-color: $colorN00;
    box-shadow: 0 0 0 3px $colorD20;
    gap: 8px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &__title {
      color: $colorN80;
      font-size: 13px;
      font-style: normal;
      font-weight: 700;
      letter-spacing: -0.05px;
      line-height: 18px;
      text-align: start;
    }

    &__error-text {
      margin-top: -4px;
      color: $colorE50;
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
      text-align: left;
    }

    &__input {
      &.error {
        border: 1px solid $colorE50;
        outline: 3px solid $colorE10;
      }
    }
  }
}
</style>
<style lang="scss">
.popover-text {
  &.is-teleported {
    padding: 0 !important;
  }
}
</style>
